/**
 * Componente de Badge para exibir etiquetas (labels)
 * 
 * Exibe uma etiqueta com cor e nome de forma consistente em toda a aplicação.
 * Suporta diferentes tamanhos e variantes.
 */

import { Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Label, getLabelColorHex, WHATSAPP_LABEL_COLORS } from '@/hooks/useLabels';

interface LabelBadgeProps {
  /** Dados da etiqueta */
  label: Label;
  /** Tamanho do badge */
  size?: 'sm' | 'md' | 'lg';
  /** Se mostra o ícone de tag */
  showIcon?: boolean;
  /** Se pode ser removido (mostra X) */
  removable?: boolean;
  /** Callback quando removido */
  onRemove?: () => void;
  /** Classes adicionais */
  className?: string;
}

/**
 * Badge para exibir uma etiqueta individual
 */
export function LabelBadge({
  label,
  size = 'sm',
  showIcon = true,
  removable = false,
  onRemove,
  className,
}: LabelBadgeProps) {
  // Determina a cor hexadecimal
  const colorHex = label.color_hex || getLabelColorHex(String(label.color_index || 0));

  // Classes de tamanho
  const sizeClasses = {
    sm: 'px-1.5 py-0 text-[10px] gap-0.5',
    md: 'px-2 py-0.5 text-xs gap-1',
    lg: 'px-2.5 py-1 text-sm gap-1.5',
  };

  // Tamanho do ícone
  const iconSizes = {
    sm: 10,
    md: 12,
    lg: 14,
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        'flex items-center font-normal transition-colors cursor-default',
        sizeClasses[size],
        removable && 'cursor-pointer hover:opacity-80',
        className
      )}
      style={{
        borderColor: colorHex,
        color: colorHex,
        backgroundColor: `${colorHex}15`,
      }}
      onClick={removable ? onRemove : undefined}
    >
      {showIcon && <Tag size={iconSizes[size]} />}
      <span className="truncate max-w-[120px]">{label.name}</span>
      {removable && (
        <span className="ml-0.5 hover:opacity-70">×</span>
      )}
    </Badge>
  );
}

// =============================================================================
// COMPONENTE DE LISTA DE BADGES
// =============================================================================

interface LabelBadgeListProps {
  /** Lista de etiquetas */
  labels: Label[];
  /** Tamanho dos badges */
  size?: 'sm' | 'md' | 'lg';
  /** Máximo de etiquetas a exibir (restante mostra "+N") */
  maxVisible?: number;
  /** Classes adicionais */
  className?: string;
}

/**
 * Lista de badges de etiquetas com limite de exibição
 */
export function LabelBadgeList({
  labels,
  size = 'sm',
  maxVisible = 3,
  className,
}: LabelBadgeListProps) {
  if (labels.length === 0) return null;

  const visibleLabels = labels.slice(0, maxVisible);
  const hiddenCount = labels.length - maxVisible;

  return (
    <div className={cn('flex flex-wrap gap-1', className)}>
      {visibleLabels.map((label) => (
        <LabelBadge key={label.id} label={label} size={size} />
      ))}
      {hiddenCount > 0 && (
        <Badge
          variant="secondary"
          className={cn(
            'font-normal',
            size === 'sm' && 'px-1.5 py-0 text-[10px]',
            size === 'md' && 'px-2 py-0.5 text-xs',
            size === 'lg' && 'px-2.5 py-1 text-sm'
          )}
        >
          +{hiddenCount}
        </Badge>
      )}
    </div>
  );
}

// =============================================================================
// COMPONENTE DE SELETOR DE COR
// =============================================================================

interface LabelColorPickerProps {
  /** Índice de cor selecionado */
  value: number;
  /** Callback quando muda */
  onChange: (colorIndex: number) => void;
  /** Classes adicionais */
  className?: string;
}

/**
 * Seletor de cor para etiquetas (cores do WhatsApp)
 */
export function LabelColorPicker({
  value,
  onChange,
  className,
}: LabelColorPickerProps) {
  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {Object.entries(WHATSAPP_LABEL_COLORS).map(([index, hex]) => (
        <button
          key={index}
          type="button"
          className={cn(
            'w-6 h-6 rounded-full border-2 transition-all',
            value === Number(index)
              ? 'border-foreground scale-110'
              : 'border-transparent hover:scale-105'
          )}
          style={{ backgroundColor: hex }}
          onClick={() => onChange(Number(index))}
          title={`Cor ${Number(index) + 1}`}
        />
      ))}
    </div>
  );
}
